import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { toast } from "sonner";

const CONSENT_KEY = "ff_cookie_consent";
const HIDDEN_PATHS = ["/embed"];

export function getCookieConsent() {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 800);
    const openPrefs = () => setVisible(true);
    window.addEventListener("ff:cookie-preferences", openPrefs);
    return () => {
      clearTimeout(t);
      window.removeEventListener("ff:cookie-preferences", openPrefs);
    };
  }, []);

  const choose = (choice) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ choice, ts: Date.now() }));
    } catch {}
    try {
      if (choice === "all") window.__ffConsent?.unblock();
      else window.__ffConsent?.block();
    } catch {}
    if (choice === "essential") {
      toast.success("Analytics off", {
        description: "You can change this anytime under \u201CCookie preferences\u201D in the footer.",
      });
    } else {
      toast.success("Preferences saved", {
        description: "All cookies enabled. Change anytime under \u201CCookie preferences\u201D in the footer.",
      });
    }
    setVisible(false);
  };

  const onHiddenPath = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  const show = visible && !onHiddenPath;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="cookie-consent-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 30 }}
          className="fixed inset-x-3 bottom-3 z-[80] mx-auto max-w-md rounded-2xl border border-ff-border bg-white p-4 shadow-2xl shadow-ff-blue/10 sm:inset-x-auto sm:left-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3F4F6] text-ff-blue">
              <Cookie className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="font-heading text-sm font-bold text-ff-blue">We value your privacy</div>
              <p className="mt-0.5 text-xs leading-relaxed text-ff-medium">
                We use cookies to keep you signed in, process donations securely, and understand how
                campaigns perform. We never sell your data. See our{" "}
                <Link to="/cookies" data-testid="cookie-consent-policy-link" className="font-semibold text-ff-blue underline underline-offset-2">
                  Cookie Policy
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              data-testid="cookie-accept-all"
              onClick={() => choose("all")}
              className="flex-1 rounded-lg bg-ff-action px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ff-actionDark"
            >
              Accept all
            </button>
            <button
              data-testid="cookie-reject-nonessential"
              onClick={() => choose("essential")}
              className="flex-1 rounded-lg border border-ff-border bg-white px-4 py-2.5 text-sm font-semibold text-ff-blue transition-colors duration-200 hover:bg-[#F3F4F6]"
            >
              Essential only
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
